/**
 * Which role a file lands under, given the pool it lands in — decision 0050,
 * from task 0166 (the canon blind gate's one FAIL).
 *
 * The DOOR decides what a file IS (`ingest.ts` maps mime → role, the one
 * place that inspects bytes). This decides where that meaning lives in THIS
 * pool: the generated collection layout binds media by pool-level coverage,
 * so a role only this object carries is a photo no collection ever shows —
 * durably stored, rendered large in the single lens, and reading as data
 * loss on every card. Both media pools' image convention is the same
 * vocabulary role (`media.poster-frame`; Films says "Poster", Archive says
 * "Frame"), so landing the image there puts it in the column the seeds
 * already ride.
 *
 * Three rules, in order, and the order is the safety:
 *  1. the pool declares the door's role → verbatim (a video into Archive
 *     stays `media.video`);
 *  2. the pool declares EXACTLY ONE other role of the same kind → that role
 *     (an image into Films becomes its Poster);
 *  3. anything else → the door's role unchanged (an image into Notes stays
 *     `media.hero`; two same-kind candidates is ambiguity, and guessing
 *     between somebody's columns is worse than the fallback).
 *
 * The same-kind test is what keeps an image out of `media.video` and
 * `media.audio` by construction — the objection 0166 recorded against this
 * option assumed the remap could cross kinds, and it cannot.
 */
import { ROLE_KIND, type Role } from '@morph/spec';

export function attachRoleFor(
  defs: ReadonlyArray<{ readonly role: string }>,
  doorRole: Role,
): Role {
  if (defs.some((d) => d.role === doorRole)) return doorRole;
  const kind = ROLE_KIND[doorRole];
  const sameKind = [...new Set(defs.map((d) => d.role as Role))]
    .filter((r) => ROLE_KIND[r] === kind);
  return sameKind.length === 1 ? sameKind[0]! : doorRole;
}
